if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          this.initializeTabs(target);
          this.initializeDesktopMedia(target);
        });
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      async show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        try {
          await this.ensureAssetsLoaded();
          const productUrl = new URL(
            opener.getAttribute('data-product-url'),
            window.location.origin
          );
          const currentParams = new URLSearchParams(window.location.search);
          ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
            if (currentParams.has(key)) productUrl.searchParams.set(key, currentParams.get(key));
          });

          const response = await fetch(productUrl.toString());
          const responseText = await response.text();
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector('product-info');

          await this.ensureAppScripts(responseHTML);
          this.preprocessHTML(productElement);
          HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
          this.executeScripts(productElement);
          this.initSwiper(this.modalContent);
          this.initializeWishlist(this.modalContent);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          super.show(opener);
        } finally {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading__spinner').classList.add('hidden');
        }
      }

      ensureAssetsLoaded() {
        if (this.assetsPromise) return this.assetsPromise;

        const assetBase = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const assetUrls = {
          sectionMainProduct: this.dataset.assetSectionMainProduct,
          componentVariantPicker: this.dataset.assetComponentVariantPicker,
          componentSwatch: this.dataset.assetComponentSwatch,
          componentPrice: this.dataset.assetComponentPrice,
          productInfo: this.dataset.assetProductInfo,
          productForm: this.dataset.assetProductForm,
        };

        const cssAssets = [
          {
            id: 'section-main-product-css',
            href: assetUrls.sectionMainProduct || `${assetBase}assets/section-main-product.css`,
          },
          {
            id: 'component-product-variant-picker-css',
            href:
              assetUrls.componentVariantPicker ||
              `${assetBase}assets/component-product-variant-picker.css`,
          },
          {
            id: 'component-swatch-css',
            href: assetUrls.componentSwatch || `${assetBase}assets/component-swatch.css`,
          },
          {
            id: 'component-price-css',
            href: assetUrls.componentPrice || `${assetBase}assets/component-price.css`,
          },
          {
            id: 'swiper-style',
            href: 'https://cdn.jsdelivr.net/npm/swiper@9/swiper-bundle.min.css',
          },
        ].filter(({ href }) => Boolean(href));

        const jsAssets = [
          {
            id: 'swiper-script',
            src: 'https://cdn.jsdelivr.net/npm/swiper@9/swiper-bundle.min.js',
          },
          {
            id: 'product-info-js',
            src: assetUrls.productInfo || `${assetBase}assets/product-info.js`,
          },
          {
            id: 'product-form-js',
            src: assetUrls.productForm || `${assetBase}assets/product-form.js`,
          },
        ].filter(({ src }) => Boolean(src));

        const loadStyles = cssAssets.map((sheet) => this.loadStylesheet(sheet));
        const loadScripts = jsAssets.map((script) => this.loadScript(script));

        this.assetsPromise = Promise.all([...loadStyles, ...loadScripts]).catch((error) => {
          this.assetsPromise = null;
          throw error;
        });

        return this.assetsPromise;
      }

      loadStylesheet({ id, href }) {
        return new Promise((resolve, reject) => {
          const existing =
            document.getElementById(id) || document.querySelector(`link[href="${href}"]`);
          if (existing) {
            resolve();
            return;
          }

          const link = document.createElement('link');
          link.id = id;
          link.rel = 'stylesheet';
          link.href = href;
          link.onload = () => resolve();
          link.onerror = reject;
          document.head.appendChild(link);
        });
      }

      loadScript({ id, src }) {
        return new Promise((resolve, reject) => {
          const existing =
            document.getElementById(id) || document.querySelector(`script[src="${src}"]`);
          const loadedChecks = {
            'swiper-script': () => typeof window.Swiper !== 'undefined',
            'product-info-js': () => customElements.get('product-info'),
            'product-form-js': () => customElements.get('product-form'),
          };

          if (existing) {
            if (existing.dataset.loaded) {
              resolve();
              return;
            }

            if (loadedChecks[id] && loadedChecks[id]()) {
              existing.dataset.loaded = 'true';
              resolve();
              return;
            }

            existing.addEventListener(
              'load',
              () => {
                existing.dataset.loaded = 'true';
                resolve();
              },
              { once: true }
            );
            existing.addEventListener('error', reject, { once: true });
            return;
          }

          const script = document.createElement('script');
          script.id = id;
          script.src = src;
          script.defer = true;
          script.onload = () => {
            script.dataset.loaded = 'true';
            resolve();
          };
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      initializeTabs(productInfoElement) {
        if (!productInfoElement) return;
        const tabs = productInfoElement.querySelectorAll('.item__tab');
        const tabPanels = productInfoElement.querySelectorAll('.item__tab-area');
        if (!tabs.length || !tabPanels.length) return;

        const setTabState = (activeId) => {
          tabs.forEach((tab) => {
            const isActive = tab.id === activeId;
            tab.classList.toggle('is-active', isActive);
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            tab.setAttribute('tabindex', isActive ? '0' : '-1');
          });

          tabPanels.forEach((panel) => {
            const isActive = panel.id === `${activeId}-area`;
            panel.classList.toggle('is-active', isActive);
            panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          });
        };

        tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            setTabState(tab.id);
            tab.focus();
          });
          tab.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setTabState(tab.id);
            }
          });
        });

        const currentTab = productInfoElement.querySelector('.item__tab.is-active') || tabs[0];
        if (currentTab) setTabState(currentTab.id);
      }

      initSwiper(root) {
        if (!window.Swiper || !root) return;
        const slider = root.querySelector('[id^="item-sp-slider-"]');
        if (!slider || slider.dataset.initialized === 'true') return;

        const sectionId = slider.id.replace('item-sp-slider-', '');
        const thumbs = root.querySelector(`#item-sp-thumbs-${sectionId}`);

        let thumbsInstance = null;
        if (thumbs) {
          thumbsInstance = new Swiper(thumbs, {
            slidesPerView: 'auto',
            spaceBetween: 8,
            freeMode: true,
            watchSlidesProgress: true,
          });
        }

        new Swiper(slider, {
          loop: true,
          slidesPerView: 1,
          spaceBetween: 12,
          centeredSlides: true,
          navigation: {
            nextEl: slider.querySelector('.swiper-button-next'),
            prevEl: slider.querySelector('.swiper-button-prev'),
          },
          thumbs: thumbsInstance ? { swiper: thumbsInstance } : undefined,
        });
        slider.dataset.initialized = 'true';
      }

      initializeDesktopMedia(root) {
        if (!root) return;
        const thumbButtons = root.querySelectorAll('[data-thumb-target]');
        const mainFrames = root.querySelectorAll('[data-target]');
        if (!thumbButtons.length || !mainFrames.length) return;

        const setActive = (targetId) => {
          mainFrames.forEach((frame) =>
            frame.classList.toggle('is-active', frame.dataset.target === targetId)
          );
          thumbButtons.forEach((thumb) =>
            thumb.classList.toggle('is-active', thumb.dataset.thumbTarget === targetId)
          );
        };

        thumbButtons.forEach((button) => {
          button.addEventListener('click', () => {
            setActive(button.dataset.thumbTarget);
          });
        });

        const current = root.querySelector('.item__images-frame.is-active') || mainFrames[0];
        if (current) setActive(current.dataset.target);
      }

      initializeWishlist(root) {
        if (!root) return;
        const productInfo = root.querySelector('product-info');
        if (!productInfo) return;

        const wishlistBtn = productInfo.querySelector('[data-wishlist-button]');
        const variantInput = productInfo.querySelector(
          `#product-form-${productInfo.dataset.section} input[name="id"]`
        );
        if (!wishlistBtn || !variantInput) return;

        const favorite = window.PrimeReviewFavorite;
        const customerId = wishlistBtn.dataset.customerId;
        const productHandle =
          productInfo.dataset.url?.split('/products/')[1]?.split('?')[0] ||
          productInfo.dataset.productId;
        const sectionId =
          productInfo.sectionId ||
          productInfo.dataset.originalSection ||
          productInfo.dataset.section;

        const parseSelectedVariant = () => {
          const json = productInfo.querySelector(
            'variant-selects [data-selected-variant]'
          )?.textContent;
          if (!json) return null;
          try {
            return JSON.parse(json);
          } catch (e) {
            console.error('Failed to parse selected variant JSON', e);
            return null;
          }
        };

        const updateWishlistButton = (variant) => {
          const variantId = variant?.id || variantInput.value;
          wishlistBtn.dataset.variantId = variantId;
          const title = variant?.title;
          const text =
            title && title !== 'Default Title' ? `${title}をお気に入りに追加` : 'お気に入りに追加';
          wishlistBtn.textContent = text;
        };

        updateWishlistButton(parseSelectedVariant());

        variantInput.addEventListener('change', () => updateWishlistButton());

        if (typeof subscribe === 'function') {
          const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data?.variant || data.sectionId !== sectionId) return;
            updateWishlistButton(data.variant);
          });
          productInfo.addEventListener('shopify:section:unload', () => unsubscribe?.(), {
            once: true,
          });
          productInfo.addEventListener(
            'DOMNodeRemoved',
            () => {
              unsubscribe?.();
            },
            { once: true }
          );
        }

        wishlistBtn.addEventListener('click', async (event) => {
          event.preventDefault();
          if (!favorite) {
            console.error('PrimeReviewFavorite is not loaded');
            return;
          }
          if (!customerId) {
            alert('ログインしてください');
            window.location.href = '/account/login';
            return;
          }
          try {
            await favorite.submitFavorite(productHandle, wishlistBtn.dataset.variantId, customerId);
            window.location.href = '/tools/prime-review/favorites.html';
          } catch (error) {
            console.error('お気に入り登録に失敗しました:', error);
          }
        });
      }

      executeScripts(source) {
        if (!source) return;
        const scripts = source.querySelectorAll('script');
        scripts.forEach((script) => {
          const newScript = document.createElement('script');
          if (script.src) {
            if (document.querySelector(`script[src="${script.src}"]`)) return;
            newScript.src = script.src;
          } else {
            newScript.textContent = script.textContent;
          }
          if (script.type) newScript.type = script.type;
          newScript.defer = script.defer;
          newScript.async = script.async;
          if (script.id && !document.getElementById(script.id)) {
            newScript.id = script.id;
          }
          document.head.appendChild(newScript);
        });
      }

      ensureAppScripts(doc) {
        if (!doc) return Promise.resolve();
        if (this.appScriptsPromise) return this.appScriptsPromise;

        const shouldLoad = (src = '') =>
          /prime[-_]review|prime review|primefavorite|favorite/i.test(src);

        const loaders = Array.from(doc.querySelectorAll('script[src]'))
          .filter(({ src }) => shouldLoad(src))
          .map((script) => {
            const existing = document.querySelector(`script[src="${script.src}"]`);
            return new Promise((resolve, reject) => {
              if (existing) {
                if (existing.dataset.loaded) {
                  resolve();
                  return;
                }
                existing.addEventListener('load', () => {
                  existing.dataset.loaded = 'true';
                  resolve();
                });
                existing.addEventListener('error', reject);
                return;
              }
              const el = document.createElement('script');
              el.src = script.src;
              el.defer = true;
              el.onload = () => {
                el.dataset.loaded = 'true';
                resolve();
              };
              el.onerror = reject;
              document.head.appendChild(el);
            });
          });

        this.appScriptsPromise = loaders.length ? Promise.all(loaders) : Promise.resolve();
        return this.appScriptsPromise;
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
